/**
 * MapExportButton - Botón para exportar vistas del mapa
 *
 * @version 1.0
 * @updated 2026-02-19
 * @module core/map-engine
 *
 * @description
 * Componente de botón con modal para exportar el mapa actual como PNG o PDF.
 * Incluye opciones de configuración de calidad, escala y metadatos.
 *
 * @remarks
 * **Características**:
 * - Exportación PNG con escala configurable (1x, 2x, 3x)
 * - Exportación PDF con metadatos
 * - Modal glassmórfico para opciones
 * - Feedback visual durante exportación
 *
 * @example
 * ```tsx
 * import { MapExportButton } from '@/core/map-engine';
 *
 * function MapControls() {
 *   return (
 *     <div className="controls">
 *       <MapExportButton />
 *     </div>
 *   );
 * }
 * ```
 */

import { useState } from 'react';
import { mapExportService } from './MapExportService';
import { useProject } from '../projects';
import './map-export-button.css';

/**
 * Componente MapExportButton
 */
export function MapExportButton(): JSX.Element {
  const { projectId: _projectId } = useProject();
  const [isOpen, setIsOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [format, setFormat] = useState<'png' | 'pdf'>('png');
  const [scale, setScale] = useState<1 | 2 | 3>(2);
  const [includeWatermark, setIncludeWatermark] = useState(true);

  /**
   * Maneja la exportación
   */
  const handleExport = async (): Promise<void> => {
    setIsExporting(true);

    try {
      const filename = `WESTEER-${new Date().toISOString().split('T')[0]}`;

      if (format === 'png') {
        await mapExportService.exportPNG({
          filename,
          scale,
          includeWatermark,
        });
      } else {
        await mapExportService.exportPDF({
          filename,
          title: 'Mapa de WESTEER',
          description: `Exportado el ${new Date().toLocaleDateString('es-AR')}`,
        });
      }

      // Cerrar modal tras exportación exitosa
      setIsOpen(false);
    } catch (error) {
      console.error('[MapExportButton] Error al exportar:', error);
      alert('Error al exportar el mapa. Ver consola para detalles.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      {/* Botón de exportación */}
      <button
        className="map-control-btn"
        onClick={() => setIsOpen(true)}
        aria-label="Exportar mapa"
        title="Exportar mapa como PNG o PDF"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M8 12 L8 4" />
          <path d="M12 8 L8 12 L4 8" />
          <path d="M3 14 L13 14" />
        </svg>
      </button>

      {/* Modal de opciones de exportación */}
      {isOpen && (
        <div className="map-export-modal-overlay" onClick={() => setIsOpen(false)}>
          <div className="map-export-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Exportar Mapa</h3>
              <button className="close-btn" onClick={() => setIsOpen(false)} aria-label="Cerrar">
                ×
              </button>
            </div>

            <div className="modal-body">
              {/* Selector de formato */}
              <div className="form-group">
                <label>Formato:</label>
                <div className="radio-group">
                  <label className="radio-option">
                    <input
                      type="radio"
                      name="format"
                      value="png"
                      checked={format === 'png'}
                      onChange={() => setFormat('png')}
                    />
                    <span>PNG (Imagen)</span>
                  </label>
                  <label className="radio-option">
                    <input
                      type="radio"
                      name="format"
                      value="pdf"
                      checked={format === 'pdf'}
                      onChange={() => setFormat('pdf')}
                    />
                    <span>PDF (Documento)</span>
                  </label>
                </div>
              </div>

              {/* Opciones específicas para PNG */}
              {format === 'png' && (
                <>
                  <div className="form-group">
                    <label>Resolución:</label>
                    <select
                      className="select-input"
                      value={scale}
                      onChange={(e) => setScale(Number(e.target.value) as 1 | 2 | 3)}
                    >
                      <option value="1">Normal (1x)</option>
                      <option value="2">Alta (2x) - Recomendado</option>
                      <option value="3">Ultra Alta (3x)</option>
                    </select>
                    <small className="help-text">
                      Mayor resolución = mejor calidad pero archivo más grande
                    </small>
                  </div>

                  <div className="form-group">
                    <label className="checkbox-option">
                      <input
                        type="checkbox"
                        checked={includeWatermark}
                        onChange={(e) => setIncludeWatermark(e.target.checked)}
                      />
                      <span>Incluir marca de agua "Generado con WESTEER"</span>
                    </label>
                  </div>
                </>
              )}

              {/* Información del proyecto */}
              <div className="export-info">
                <div className="info-item">
                  <span className="info-label">Proyecto:</span>
                  <span className="info-value">WESTEER</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Fecha:</span>
                  <span className="info-value">{new Date().toLocaleDateString('es-AR')}</span>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setIsOpen(false)} disabled={isExporting}>
                Cancelar
              </button>
              <button className="btn-primary" onClick={handleExport} disabled={isExporting}>
                {isExporting ? 'Exportando...' : `Exportar ${format.toUpperCase()}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
